import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Avatar, cn } from '@immo/ui-kit';
import type { AdminMeResponse } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';

type Props = {
  readonly me: AdminMeResponse;
  readonly collapsed?: boolean;
};

export function UserMenu({ me, collapsed = false }: Props) {
  const navigate = useNavigate();
  const t = useTranslations('pages.admin.shell.user-menu');
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ bottom: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || triggerRef.current === null) {
      return;
    }

    const rect = triggerRef.current.getBoundingClientRect();

    // Anchor the popover above the trigger so it floats up into the sidebar.
    setPosition({ bottom: window.innerHeight - rect.top + 4, left: rect.left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = triggerRef.current !== null && triggerRef.current.contains(target);
      const inMenu = menuRef.current !== null && menuRef.current.contains(target);

      if (!inTrigger && !inMenu) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onClick);

    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const initials = formatInitials(me);
  const displayName = formatDisplayName(me);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={displayName}
        className={cn(
          'flex w-full items-center rounded-md py-1.5 text-left text-base text-text-primary transition-colors duration-micro ease-out hover:bg-hover-bg focus-visible:outline-none focus-visible:shadow-focus',
          collapsed ? 'justify-center px-0' : 'gap-2 px-2',
        )}
      >
        <Avatar initials={initials} seed={me.user.email} size="sm" />
        {!collapsed && <span className="min-w-0 flex-1 truncate text-base-sm font-medium">{displayName}</span>}
      </button>
      {open &&
        position !== null &&
        createPortal(
          <div
            ref={menuRef}
            style={{ bottom: position.bottom, left: position.left }}
            className="fixed z-50 w-56 overflow-hidden rounded-lg border border-border bg-surface shadow-md"
          >
            <div className="border-b border-border px-3 py-2">
              <div className="text-base font-medium text-text-primary">{displayName}</div>
              <div className="truncate text-sm text-text-tertiary">{me.user.email}</div>
            </div>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/sign-out');
              }}
              className="block w-full px-3 py-2 text-left text-base text-text-primary transition-colors duration-micro ease-out hover:bg-hover-bg"
            >
              {t('sign-out')}
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}

function formatDisplayName(me: AdminMeResponse): string {
  const parts = [me.user.firstName, me.user.lastName].filter((p): p is string => p !== null && p.length > 0);

  return parts.length > 0 ? parts.join(' ') : me.user.email;
}

function formatInitials(me: AdminMeResponse): string {
  const name = formatDisplayName(me);
  const tokens = name.split(/\s+/).filter((t) => t.length > 0);

  if (tokens.length === 0) return '?';

  if (tokens.length === 1) return tokens[0]!.charAt(0).toUpperCase();

  return (tokens[0]!.charAt(0) + tokens[tokens.length - 1]!.charAt(0)).toUpperCase();
}
